/**
 * @file Ecran Skins : "Chats" (24 skins, apercu colore, bonus cumules, achat argent/gemmes, equipement), "Munitions" (skins cosmetiques
 * de balles par categorie d'arme), "FX de tir" et "Familiers" (achat en Pieces de familier, 3 emplacements).
 */
import type { ModifierDef, SkinDef } from '@config/types';
import { Decimal } from '@shared/bignum';
import type { StatId, WalletId } from '@shared/enums';
import { formatMult } from '@shared/format';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { confirmAsk, send } from '../core/feedback';
import { hatIcon } from '../core/hatIcons';
import { balanceOf, makeTabs, sheetHeader, walletChips } from '../core/widgets';
import type { TabsHandle } from '../core/widgets';
import type { UiContext } from '../UiContext';
import { AmmoPane } from './skins/AmmoPane';
import { PetsPane } from './skins/PetsPane';
import { ShotFxPane } from './skins/ShotFxPane';

interface CardView {
  skin: SkinDef;
  root: HTMLElement;
  state: HTMLElement;
  costs: HTMLElement;
}

/** "+5%" pour un bonus multiplicatif 1.05 ou additif 0.05. */
const bonusPercent = (m: ModifierDef): number => {
  const v = Decimal.fromValue(m.value).toNumber();
  return Math.round((m.op === 'mult' ? v - 1 : v) * 1000) / 10;
};

type TabId = 'cats' | 'ammo' | 'shotfx' | 'pets';

export class SkinsScreen extends Component {
  private tabs!: TabsHandle<TabId>;
  private ammo!: AmmoPane;
  private catsPane!: HTMLElement;
  private ammoPane!: HTMLElement;
  private shotFx!: ShotFxPane;
  private shotFxPane!: HTMLElement;
  private pets!: PetsPane;
  private petsPane!: HTMLElement;
  private readonly cards: CardView[] = [];
  private totals!: HTMLElement;
  private totalStats: StatId[] = [];
  private totalValues = new Map<StatId, HTMLElement>();

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'screen skins');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');
    const grid = h('div', { class: 'skin-grid' });
    const stats = new Set<StatId>();

    for (const skin of this.ctx.config.skins) {
      const bonus = skin.bonus.map((b) => {
        stats.add(b.stat);
        return `${t.tDyn(`stat.${b.stat}`)} +${bonusPercent(b)}%`;
      });
      const cat = h(
        'div',
        { class: `skin-cat${skin.stripes ? ' striped' : ''}`, style: `--fur:${skin.fur};--stripe:${skin.stripes ?? skin.fur};--outfit:${skin.outfitColor ?? 'transparent'}` },
        h('i', { class: 'ear l' }),
        h('i', { class: 'ear r' }),
        h('div', { class: 'face' }, h('i', { class: 'eye' }), h('i', { class: 'eye' })),
        h('i', { class: `outfit outfit-${skin.outfit}` }),
        h('span', { class: 'skin-hat' }, hatIcon(skin.hat)),
      );
      const state = h('div', { class: 'skin-state' });
      const costs = h('div', { class: 'skin-costs' }, ...walletChips(this.ctx, skin.cost));
      const root = h(
        'button',
        { class: 'skin-card', type: 'button', 'data-ui-block': true, 'data-skin': skin.id, onclick: () => void this.activate(skin) },
        cat,
        h('div', { class: 'skin-name' }, t.name(skin.name)),
        h('div', { class: 'skin-bonus' }, bonus.join(' · ')),
        state,
        costs,
      );
      this.cards.push({ skin, root, state, costs });
      grid.append(root);
    }

    // Totaux des bonus de skins (par stat).
    this.totalStats = [...stats];
    this.totals = h('div', { class: 'skin-totals panel' }, h('div', { class: 'settings-label' }, t.t('skins.total')));
    const line = h('div', { class: 'chips-row' });
    for (const stat of this.totalStats) {
      const value = h('b', {});
      this.totalValues.set(stat, value);
      line.append(h('span', { class: 'chip' }, t.tDyn(`stat.${stat}`), ' ', value));
    }
    this.totals.append(line, h('p', { class: 'hint' }, t.t('skins.bonusHint')));

    this.catsPane = h('div', { class: 'pane pane-cats' }, grid, this.totals);
    this.ammoPane = h('div', { class: 'pane pane-ammo' });
    this.ammo = new AmmoPane(this.ctx).mount(this.ammoPane);
    this.addDisposer(() => this.ammo.destroy());
    this.shotFxPane = h('div', { class: 'pane pane-shotfx' });
    this.shotFx = new ShotFxPane(this.ctx).mount(this.shotFxPane);
    this.addDisposer(() => this.shotFx.destroy());
    this.petsPane = h('div', { class: 'pane pane-pets' });
    this.pets = new PetsPane(this.ctx).mount(this.petsPane);
    this.addDisposer(() => this.pets.destroy());
    this.tabs = makeTabs<TabId>(
      [
        { id: 'cats', label: t.t('skins.tab.cats') },
        { id: 'ammo', label: t.t('skins.tab.ammo') },
        { id: 'shotfx', label: t.t('skins.tab.shotfx') },
        { id: 'pets', label: t.t('skins.tab.pets') },
      ],
      'cats',
      () => this.onTab(),
    );
    this.el.append(sheetHeader(this.ctx, t.t('skins.title')), this.tabs.el, h('div', { class: 'sheet-body' }, this.catsPane, this.ammoPane, this.shotFxPane, this.petsPane));
    this.onTab();
  }

  private onTab(): void {
    const a = this.tabs.active();
    toggleClass(this.catsPane, 'active', a === 'cats');
    toggleClass(this.ammoPane, 'active', a === 'ammo');
    toggleClass(this.shotFxPane, 'active', a === 'shotfx');
    toggleClass(this.petsPane, 'active', a === 'pets');
    this.update(0);
  }

  private async activate(skin: SkinDef): Promise<void> {
    const { data } = this.ctx.sim;
    if (data.skins.owned.includes(skin.id)) {
      if (data.skins.equipped !== skin.id) void send(this.ctx, { type: 'skin.equip', id: skin.id });
      return;
    }
    if (skin.cost.dia !== undefined && !(await confirmAsk('skins.confirmGems'))) return;
    void send(this.ctx, { type: 'skin.buy', id: skin.id });
  }

  override update(dt: number): void {
    if (this.tabs.active() === 'ammo') {
      this.ammo.update(dt);
      return;
    }
    if (this.tabs.active() === 'shotfx') {
      this.shotFx.update(dt);
      return;
    }
    if (this.tabs.active() === 'pets') {
      this.pets.update(dt);
      return;
    }
    const { data, query } = this.ctx.sim;
    const { i18n } = this.ctx;
    for (const c of this.cards) {
      const owned = data.skins.owned.includes(c.skin.id);
      const equipped = data.skins.equipped === c.skin.id;
      toggleClass(c.root, 'owned', owned);
      toggleClass(c.root, 'equipped', equipped);
      toggleClass(c.costs, 'is-hidden', owned);
      setText(c.state, equipped ? i18n.t('common.equipped') : owned ? i18n.t('common.equip') : '');
      const affordable = (Object.keys(c.skin.cost) as WalletId[]).every((id) => {
        const v = c.skin.cost[id];
        return v === undefined || balanceOf(this.ctx, id).gte(Decimal.fromValue(v));
      });
      toggleClass(c.root, 'disabled', !owned && !affordable);
    }
    for (const stat of this.totalStats) {
      const el = this.totalValues.get(stat);
      if (!el) continue;
      let mult = new Decimal(1);
      let add = new Decimal(0);
      for (const m of query.statBreakdown(stat).modifiers) {
        if (!m.source.startsWith('skin:')) continue;
        if (m.op === 'mult') mult = mult.times(m.value);
        else add = add.plus(m.value);
      }
      setText(el, add.eq(0) ? formatMult(mult) : `${formatMult(mult)} +${add.times(100).toFixed(0)}%`);
    }
  }
}
